/**
 * The only channel between a running game and the studio shell.
 *
 * The game lives on a separate origin inside a sandboxed iframe with an opaque
 * origin, so `event.origin` on the shell side is the string "null" and cannot be
 * used to authenticate anything. The shell therefore trusts the *shape* of the
 * message and nothing else: unknown types are dropped and logged, and no message
 * from a game is ever given authority over credits, time or publishing.
 */

export const GAME_TO_SHELL = [
  'ready',
  'error',
  'score',
  'win',
  'lose',
  'request-restart',
] as const;

export const SHELL_TO_GAME = ['start', 'pause', 'resume', 'restart'] as const;

export type GameToShellType = (typeof GAME_TO_SHELL)[number];
export type ShellToGameType = (typeof SHELL_TO_GAME)[number];

export type GameToShellMessage =
  | { type: 'ready' }
  | { type: 'error'; message: string; stack?: string }
  | { type: 'score'; value: number }
  | { type: 'win' }
  | { type: 'lose' }
  | { type: 'request-restart' };

/** Namespace tag so we ignore stray postMessages from anything else. */
export const CHANNEL = 'kids-studio/v1' as const;

export type Envelope<T> = { channel: typeof CHANNEL; payload: T };

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

/**
 * Validate a message arriving from a game. Deliberately strict and total: every
 * field is checked and anything unrecognised returns null so the caller logs and
 * drops it. Never widen this without also widening GAME_TO_SHELL.
 */
export function parseGameMessage(data: unknown): GameToShellMessage | null {
  if (!isRecord(data) || data['channel'] !== CHANNEL) return null;
  const payload = data['payload'];
  if (!isRecord(payload)) return null;

  const type = payload['type'];
  if (typeof type !== 'string') return null;
  if (!(GAME_TO_SHELL as readonly string[]).includes(type)) return null;

  switch (type as GameToShellType) {
    case 'ready':
    case 'win':
    case 'lose':
    case 'request-restart':
      return { type } as GameToShellMessage;
    case 'score': {
      const value = payload['value'];
      if (typeof value !== 'number' || !Number.isFinite(value)) return null;
      return { type: 'score', value };
    }
    case 'error': {
      const message = payload['message'];
      if (typeof message !== 'string') return null;
      const stack = payload['stack'];
      return {
        type: 'error',
        // Cap attacker-controlled strings before they reach our logs.
        message: message.slice(0, 500),
        ...(typeof stack === 'string' ? { stack: stack.slice(0, 2000) } : {}),
      };
    }
  }
}
